import { toLower } from "lodash";
import moment from "moment-timezone";
import { fromUtcToTimeZone, asTimeZone } from "../utils/dateFormatter";
import { create, updateField, updateFields, update, remove, getById, getAll, getBySingleQuery, getByIdQuery, getByDoubleQuery, getByIdDoubleQuery } from "../utils/dbUtils";
import { isObjectNotEmpty, isStringNotEmpty, isListNotEmpty, isNumberGreaterThanOrEqualToZero, isNotNullOrUndefined, isBooleanTrue, isStringEmpty, isNumberEmpty, isNullOrUndefined, isNumberNotEmpty, isBooleanFalse, isStringNotEmptyOrZeroLength } from "../utils/objectUtils";
import { randomSubscriptionNumberGenerator } from "../utils/orderUtils";
import { toE164PhoneNumber, toPhoneNumberLocal } from "../utils/stringFormatter";
import Address from "./Address";
import Customer from "./Customer";
import Order from "./Order";
import StatusHistory from "./StatusHistory";
import { auth, db } from "../firebase";
import { collection, endBefore, getCountFromServer, getDocs, limit, limitToLast, orderBy, query, startAfter, where } from "firebase/firestore";
import EmailTemplate from "./EmailTemplate";
import UserNote from "./UserNote";
import Encounter from "./Encounter";
import Prescription from "./Prescription";
import Task from "./Task";
import { cancelBelugaVisit } from "../firebase/Beluga";
import { sendEmailToIT } from "../firebase/Email";

class Subscription {
    static collectionName = 'subscriptions';

    subscriptionNumber = randomSubscriptionNumberGenerator(15);
    status = "PENDING"; // enum SubscriptionStatuses
    statusHistory = []; // list of StatusHistory, historical changes to status
    userId = null; // userId of the customer
    coveredEntityId = null; // same as practiceId
    customer = new Customer(null).toDto(); // customer details snapshot
    userRefId = null; // refId of the customer in Advanced MD or EHR system
    externalRefs = {}; // mirror of user.externalRefs (kept in sync by the trackUserChanges trigger); read during shipment processing to skip redundant pharmacy patient-create calls

    discountTotal = 0.0; // total discounts to be subtracted from order to calculate subtotal
    discountTax = 0.0; // tax to be subtracted from tax total

    shippingTotal = 0.0; // total for shipping method (w/o tax)
    shippingTax = 0.0; // tax for shipping method

    feeTotal = 0.0; // total for all fee line items (w/o tax)
    feeTax = 0.0; // tax for all fee line items

    cartTotal = 0.0; // total of all order line items (w/o tax)
    cartTax = 0.0; // tax for all order line items

    subtotal = 0.0; // subtotal for the order
    tax = 0.0; // total tax for the order
    total = 0.0; // overall order total

    lineItems = [];
    taxLines = [];
    shippingLines = [];
    feeLines = [];
    discountLines = [];
    notes = [];
    metaData = []; // array of order meta data

    parentOrderId = null; // a subscription can only have one parent order (the order that initiated the subscription with optional sign-up fee and first order), but a subscription can also have no parent order if the subscription is future dated and no order is placed at the time the subscription is setup
    relatedOrderIds = []; // includes parent order, renewal orders

    billingAddress = new Address(null).toDto();
    shippingAddress = new Address(null).toDto();

    paymentMethods = []; // array of PaymentDetails

    customerIPAddress = null;
    customerUserAgent = null;

    customerNote = null; // note provided by the customer at the time they placed the subscription

    startDate = moment().tz('US/Pacific').startOf('day').utc().toDate(); // Subscription start date
    nextPaymentDate = null; // Subscription next payment date
    lastPaymentDate = null; // Subscription's most recent parent or renewal order created date
    endDate = null; // Subscription end date (defaults to null when the subscription has no end)
    cancelledDate = null;
    failedOrderAt = null; // orderedAt date of the first order with a failed payment that has not yet been resolved
    suspendedAt = null; // date when subscription transitions to SUSPENDED status

    billingPeriod = "MONTH";
    billingInterval = 1;
    billingCycles = 0;

    cancelledEmailSent = false;
    welcomeEmailSent = false;
    welcomeCallStatus = 'PENDING'; // enum CallStatuses
    welcomeCallDueAt = null;
    welcomeCallId = null;
    bypassBillingStatus = false;

    source = null;
    affiliateId = null;
    subAffiliateId = null;
    campaignId = null;
    programId = null;
    productSku = null;
    productFamilyId = null;
    offerGroupId = null;
    offerToken = null;
    commercialEquivalencyGroupId = null;
    productId = null;
    variantId = null;

    conversionId = null;
    isConversion = false;

    affiliateInfo = null;
    deviceInfo = null;
    emailInfo = null;
    phoneInfo = null;

    sentEmails = [];

    isStacked = false;
    stackedOptions = []; // { productCategoryId, (productId, variantId), isRequired }
    stackedSelections = []; // filled out based on what the clinician actually prescribed { productCategoryId, (productId, variantId) }
    stackEncounters = false;
    stackShipments = false;

    isCustomerRescheduled = false; // true while the next fill date is the one the customer chose; a fill clears it
    rescheduleHistory = []; // array of RescheduleHistoryEntry

    forms = []; // array of SubscriptionForm
    formsCount = 0;

    requiresConfirmation = false; // requires patient confirmation - needs to be set to true if there are surveys and they need to be filled out for each order
    requiresClinicianApproval = false; // requires that the doctor prescribes/approves the product
    requiresClinicianEncounter = false; // requires an in-person or telehealth dr appt
    requiresSurveys = false; // requires patient to fill out forms/surveys

    // require documents to be provided when ordering product
    requiredDocuments = []; // array of object { documentType, requirement, scope } requirement: enum DocumentRequirements, scope: enum SurveyScopes

    leadId = null;

    pharmacyId = null; // pharmacy to use if this subscription is for prescription product

    bundleShipmentCount = 1;
    ordersPerEncounter = 1; // how many orders can be approved at the same time in one encounter
    bundlePaymentCount = 1; // how many orders should be paid for together in a bundled up-front payment
    bundlePaymentCouponCode = null;

    encounterType = 'NONE';
    encounterFrequency = 1;
    encounterPrice = 0.0;

    nextShipmentDate = null;
    lastShipmentDate = null;

    nextEncounterDate = null;
    lastEncounterDate = null;

    nextVariantLevel = 1;
    lastVariantLevel = 1;

    repId = 'NONE'; // userId of customer representative

    includesUpcharge = false;
    upcharge = 0;

    testMode = false;

    // Metadata for cancellation and pause details
    metadata = {}; // stores cancellation details, pause details, unsettledReactivationCharge { reason, orderId, sentAt }, deniedByReferral { encounterId, deniedAt }, etc.

    // Test schedule (snapshot-copied from Product at subscription creation)
    testSchedule = []; // TestScheduleEntry[]

    // Annual cycle tracking
    cycleStartDate = null;
    currentCycleYear = 1;
    baselineCompleted = false;

    // Next/last test tracking
    nextTestDate = null;
    lastTestDate = null;
    nextScheduleEntryId = null;

    // Completed visits in current cycle
    completedVisits = []; // CompletedVisit[]

    // Active treatment tracking (for computing quarterly treatment panels)
    activeTreatmentProductIds = [];

    constructor(subscription) {
        if (isObjectNotEmpty(subscription)) {
            if (isStringNotEmpty(subscription.subscriptionNumber)) { this.subscriptionNumber = subscription.subscriptionNumber }

            if (isStringNotEmpty(subscription.status)) { this.status = subscription.status; }
            if (isListNotEmpty(subscription.statusHistory)) { this.statusHistory = subscription.statusHistory; }
            else if (isStringNotEmpty(this.status)) { this.statusHistory = StatusHistory.addNewStatus(this.status, []); }

            if (isStringNotEmpty(subscription.userId)) { this.userId = subscription.userId; }
            if (isStringNotEmpty(subscription.coveredEntityId)) { this.coveredEntityId = subscription.coveredEntityId; }
            if (isObjectNotEmpty(subscription.customer)) { this.customer = subscription.customer; }
            if (isStringNotEmpty(subscription.userRefId)) { this.userRefId = subscription.userRefId; }
            if (isObjectNotEmpty(subscription.externalRefs)) { this.externalRefs = subscription.externalRefs; }

            if (isNumberGreaterThanOrEqualToZero(subscription.discountTotal)) { this.discountTotal = subscription.discountTotal; }
            if (isNumberGreaterThanOrEqualToZero(subscription.discountTax)) { this.discountTax = subscription.discountTax; }
            if (isNumberGreaterThanOrEqualToZero(subscription.shippingTotal)) { this.shippingTotal = subscription.shippingTotal; }
            if (isNumberGreaterThanOrEqualToZero(subscription.shippingTax)) { this.shippingTax = subscription.shippingTax; }
            if (isNumberGreaterThanOrEqualToZero(subscription.feeTotal)) { this.feeTotal = subscription.feeTotal; }
            if (isNumberGreaterThanOrEqualToZero(subscription.feeTax)) { this.feeTax = subscription.feeTax; }
            if (isNumberGreaterThanOrEqualToZero(subscription.cartTotal)) { this.cartTotal = subscription.cartTotal; }
            if (isNumberGreaterThanOrEqualToZero(subscription.cartTax)) { this.cartTax = subscription.cartTax; }
            if (isNumberGreaterThanOrEqualToZero(subscription.subtotal)) { this.subtotal = subscription.subtotal; }
            if (isNumberGreaterThanOrEqualToZero(subscription.tax)) { this.tax = subscription.tax; }
            if (isNumberGreaterThanOrEqualToZero(subscription.total)) { this.total = subscription.total; }

            if (isListNotEmpty(subscription.lineItems)) { this.lineItems = subscription.lineItems; }
            if (isListNotEmpty(subscription.taxLines)) { this.taxLines = subscription.taxLines; }
            if (isListNotEmpty(subscription.shippingLines)) { this.shippingLines = subscription.shippingLines; }
            if (isListNotEmpty(subscription.feeLines)) { this.feeLines = subscription.feeLines; }
            if (isListNotEmpty(subscription.discountLines)) { this.discountLines = subscription.discountLines; }
            if (isListNotEmpty(subscription.notes)) { this.notes = subscription.notes; }
            if (isListNotEmpty(subscription.metaData)) { this.metaData = subscription.metaData; }

            if (isStringNotEmpty(subscription.parentOrderId)) { this.parentOrderId = subscription.parentOrderId; }
            if (isListNotEmpty(subscription.relatedOrderIds)) { this.relatedOrderIds = subscription.relatedOrderIds; }

            if (isObjectNotEmpty(subscription.billingAddress)) { this.billingAddress = subscription.billingAddress; }
            if (isObjectNotEmpty(subscription.shippingAddress)) { this.shippingAddress = subscription.shippingAddress; }

            if (isListNotEmpty(subscription.paymentMethods)) { this.paymentMethods = subscription.paymentMethods; }

            if (isStringNotEmpty(subscription.customerIPAddress)) { this.customerIPAddress = subscription.customerIPAddress; }
            if (isStringNotEmpty(subscription.customerUserAgent)) { this.customerUserAgent = subscription.customerUserAgent; }

            if (isStringNotEmpty(subscription.customerNote)) { this.customerNote = subscription.customerNote; }

            if (isNotNullOrUndefined(subscription.startDate)) { this.startDate = subscription.startDate; }
            if (isNotNullOrUndefined(subscription.nextPaymentDate)) { this.nextPaymentDate = subscription.nextPaymentDate; }
            if (isNotNullOrUndefined(subscription.lastPaymentDate)) { this.lastPaymentDate = subscription.lastPaymentDate; }
            if (isNotNullOrUndefined(subscription.endDate)) { this.endDate = subscription.endDate; }
            if (isNotNullOrUndefined(subscription.cancelledDate)) { this.cancelledDate = subscription.cancelledDate; }
            if (isNotNullOrUndefined(subscription.failedOrderAt)) { this.failedOrderAt = subscription.failedOrderAt; }
            if (isNotNullOrUndefined(subscription.suspendedAt)) { this.suspendedAt = subscription.suspendedAt; }

            if (isStringNotEmpty(subscription.billingPeriod)) { this.billingPeriod = subscription.billingPeriod; }
            if (isNumberGreaterThanOrEqualToZero(subscription.billingInterval)) { this.billingInterval = subscription.billingInterval; }
            if (isNumberGreaterThanOrEqualToZero(subscription.billingCycles)) { this.billingCycles = subscription.billingCycles; }

            if (isBooleanTrue(subscription.cancelledEmailSent)) { this.cancelledEmailSent = true; }
            if (isBooleanTrue(subscription.welcomeEmailSent)) { this.welcomeEmailSent = true; }
            if (isStringNotEmpty(subscription.welcomeCallStatus)) { this.welcomeCallStatus = subscription.welcomeCallStatus; }
            if (isNotNullOrUndefined(subscription.welcomeCallDueAt)) { this.welcomeCallDueAt = subscription.welcomeCallDueAt; }
            if (isStringNotEmpty(subscription.welcomeCallId)) { this.welcomeCallId = subscription.welcomeCallId; }
            if (isBooleanTrue(subscription.bypassBillingStatus)) { this.bypassBillingStatus = true; }

            if (isStringNotEmpty(subscription.source)) { this.source = subscription.source; }
            if (isStringNotEmpty(subscription.affiliateId)) { this.affiliateId = toLower(subscription.affiliateId); }
            if (isStringNotEmpty(subscription.subAffiliateId)) { this.subAffiliateId = subscription.subAffiliateId; }
            if (isStringNotEmpty(subscription.campaignId)) { this.campaignId = subscription.campaignId; }
            if (isStringNotEmpty(subscription.programId)) { this.programId = subscription.programId; }
            if (isStringNotEmpty(subscription.productSku)) { this.productSku = subscription.productSku; }
            if (isStringNotEmpty(subscription.productFamilyId)) { this.productFamilyId = subscription.productFamilyId; }
            if (isStringNotEmpty(subscription.offerGroupId)) { this.offerGroupId = subscription.offerGroupId; }
            if (isStringNotEmpty(subscription.offerToken)) { this.offerToken = subscription.offerToken; }
            if (isStringNotEmpty(subscription.commercialEquivalencyGroupId)) { this.commercialEquivalencyGroupId = subscription.commercialEquivalencyGroupId; }
            if (isStringNotEmpty(subscription.productId)) { this.productId = subscription.productId; }
            if (isStringNotEmpty(subscription.variantId)) { this.variantId = subscription.variantId; }

            if (isListNotEmpty(subscription.sentEmails)) { this.sentEmails = subscription.sentEmails; }

            if (isBooleanTrue(subscription.isStacked)) { this.isStacked = true; }
            if (isListNotEmpty(subscription.stackedOptions)) { this.stackedOptions = subscription.stackedOptions; }
            if (isListNotEmpty(subscription.stackedSelections)) { this.stackedSelections = subscription.stackedSelections; }
            if (isBooleanTrue(subscription.stackEncounters)) { this.stackEncounters = true; }
            if (isBooleanTrue(subscription.stackShipments)) { this.stackShipments = true; }

            if (isBooleanTrue(subscription.isCustomerRescheduled)) { this.isCustomerRescheduled = true; }
            if (isListNotEmpty(subscription.rescheduleHistory)) { this.rescheduleHistory = subscription.rescheduleHistory; }

            if (isListNotEmpty(subscription.forms)) {
                this.forms = subscription.forms;
                this.formsCount = subscription.forms.length;
            }

            if (isBooleanTrue(subscription.requiresConfirmation)) { this.requiresConfirmation = true; }
            if (isBooleanTrue(subscription.requiresClinicianApproval)) { this.requiresClinicianApproval = true; }
            if (isBooleanTrue(subscription.requiresClinicianEncounter)) { this.requiresClinicianEncounter = true; }
            if (isBooleanTrue(subscription.requiresSurveys)) { this.requiresSurveys = true; }

            if (isListNotEmpty(subscription.requiredDocuments)) { this.requiredDocuments = subscription.requiredDocuments; }

            if (isStringNotEmpty(subscription.conversionId)) { this.conversionId = subscription.conversionId; }
            if (isBooleanTrue(subscription.isConversion)) { this.isConversion = true; }

            if (isObjectNotEmpty(subscription.affiliateInfo)) { this.affiliateInfo = subscription.affiliateInfo; }
            if (isObjectNotEmpty(subscription.deviceInfo)) { this.deviceInfo = subscription.deviceInfo; }
            if (isObjectNotEmpty(subscription.emailInfo)) { this.emailInfo = subscription.emailInfo; }
            if (isObjectNotEmpty(subscription.phoneInfo)) { this.phoneInfo = subscription.phoneInfo; }

            if (isStringNotEmpty(subscription.leadId)) { this.leadId = subscription.leadId; }

            if (isStringNotEmpty(subscription.pharmacyId)) { this.pharmacyId = subscription.pharmacyId; }

            if (isNumberGreaterThanOrEqualToZero(subscription.bundleShipmentCount)) { this.bundleShipmentCount = subscription.bundleShipmentCount; }
            if (isNumberGreaterThanOrEqualToZero(subscription.ordersPerEncounter)) { this.ordersPerEncounter = subscription.ordersPerEncounter; }
            if (isNumberGreaterThanOrEqualToZero(subscription.bundlePaymentCount)) { this.bundlePaymentCount = subscription.bundlePaymentCount; }
            if (isStringNotEmpty(subscription.bundlePaymentCouponCode)) { this.bundlePaymentCouponCode = subscription.bundlePaymentCouponCode; }

            if (isStringNotEmpty(subscription.encounterType)) { this.encounterType = subscription.encounterType; }
            if (isNumberGreaterThanOrEqualToZero(subscription.encounterFrequency)) { this.encounterFrequency = subscription.encounterFrequency; }
            if (isNumberGreaterThanOrEqualToZero(subscription.encounterPrice)) { this.encounterPrice = subscription.encounterPrice; }

            if (isNotNullOrUndefined(subscription.nextShipmentDate)) { this.nextShipmentDate = subscription.nextShipmentDate; }
            if (isNotNullOrUndefined(subscription.lastShipmentDate)) { this.lastShipmentDate = subscription.lastShipmentDate; }

            if (isNotNullOrUndefined(subscription.nextEncounterDate)) { this.nextEncounterDate = subscription.nextEncounterDate; }
            if (isNotNullOrUndefined(subscription.lastEncounterDate)) { this.lastEncounterDate = subscription.lastEncounterDate; }

            if (isNumberGreaterThanOrEqualToZero(subscription.nextVariantLevel)) { this.nextVariantLevel = subscription.nextVariantLevel; }
            if (isNumberGreaterThanOrEqualToZero(subscription.lastVariantLevel)) { this.lastVariantLevel = subscription.lastVariantLevel; }

            if (isStringNotEmpty(subscription.repId)) { this.repId = subscription.repId; }

            if (isBooleanTrue(subscription.includesUpcharge)) { this.includesUpcharge = true; }
            if (isNumberGreaterThanOrEqualToZero(subscription.upcharge)) { this.upcharge = subscription.upcharge; }

            if (isBooleanTrue(subscription.testMode)) { this.testMode = true; }

            // Metadata
            if (isObjectNotEmpty(subscription.metadata)) { this.metadata = subscription.metadata; }

            // Test schedule
            if (isListNotEmpty(subscription.testSchedule)) { this.testSchedule = subscription.testSchedule; }

            // Annual cycle tracking
            if (isNotNullOrUndefined(subscription.cycleStartDate)) { this.cycleStartDate = subscription.cycleStartDate; }
            if (isNumberGreaterThanOrEqualToZero(subscription.currentCycleYear)) { this.currentCycleYear = subscription.currentCycleYear; }
            if (isBooleanTrue(subscription.baselineCompleted)) { this.baselineCompleted = true; }

            // Next/last test tracking
            if (isNotNullOrUndefined(subscription.nextTestDate)) { this.nextTestDate = subscription.nextTestDate; }
            if (isNotNullOrUndefined(subscription.lastTestDate)) { this.lastTestDate = subscription.lastTestDate; }
            if (isStringNotEmpty(subscription.nextScheduleEntryId)) { this.nextScheduleEntryId = subscription.nextScheduleEntryId; }

            // Completed visits
            if (isListNotEmpty(subscription.completedVisits)) { this.completedVisits = subscription.completedVisits; }

            // Active treatment tracking
            if (isListNotEmpty(subscription.activeTreatmentProductIds)) { this.activeTreatmentProductIds = subscription.activeTreatmentProductIds; }
        }
    }

    toDto = () => {
        return {
            subscriptionNumber: this.subscriptionNumber,
            status: this.status,
            statusHistory: this.statusHistory,

            userId: this.userId,
            coveredEntityId: this.coveredEntityId,
            customer: this.customer,
            userRefId: this.userRefId,
            externalRefs: this.externalRefs,

            discountTotal: this.discountTotal,
            discountTax: this.discountTax,

            shippingTotal: this.shippingTotal,
            shippingTax: this.shippingTax,

            feeTotal: this.feeTotal,
            feeTax: this.feeTax,

            cartTotal: this.cartTotal,
            cartTax: this.cartTax,

            subtotal: this.subtotal,
            tax: this.tax,
            total: this.total,

            lineItems: this.lineItems,
            taxLines: this.taxLines,
            shippingLines: this.shippingLines,
            feeLines: this.feeLines,
            discountLines: this.discountLines,
            notes: this.notes,
            metaData: this.metaData,

            parentOrderId: this.parentOrderId,
            relatedOrderIds: this.relatedOrderIds,

            billingAddress: this.billingAddress,
            shippingAddress: this.shippingAddress,

            paymentMethods: this.paymentMethods,

            customerIPAddress: this.customerIPAddress,
            customerUserAgent: this.customerUserAgent,

            customerNote: this.customerNote,

            startDate: this.startDate,
            nextPaymentDate: this.nextPaymentDate,
            lastPaymentDate: this.lastPaymentDate,
            endDate: this.endDate,
            cancelledDate: this.cancelledDate,
            failedOrderAt: this.failedOrderAt,
            suspendedAt: this.suspendedAt,

            billingPeriod: this.billingPeriod,
            billingInterval: this.billingInterval,
            billingCycles: this.billingCycles,

            cancelledEmailSent: this.cancelledEmailSent,
            welcomeEmailSent: this.welcomeEmailSent,
            welcomeCallStatus: this.welcomeCallStatus,
            welcomeCallDueAt: this.welcomeCallDueAt,
            welcomeCallId: this.welcomeCallId,
            bypassBillingStatus: this.bypassBillingStatus,

            source: this.source,
            affiliateId: this.affiliateId,
            subAffiliateId: this.subAffiliateId,
            campaignId: this.campaignId,
            programId: this.programId,
            productSku: this.productSku,
            productFamilyId: this.productFamilyId,
            offerGroupId: this.offerGroupId,
            offerToken: this.offerToken,
            commercialEquivalencyGroupId: this.commercialEquivalencyGroupId,
            productId: this.productId,
            variantId: this.variantId,

            isStacked: this.isStacked,
            stackedOptions: this.stackedOptions,
            stackedSelections: this.stackedSelections,
            stackEncounters: this.stackEncounters,
            stackShipments: this.stackShipments,

            isCustomerRescheduled: this.isCustomerRescheduled,
            rescheduleHistory: this.rescheduleHistory,

            sentEmails: this.sentEmails,

            forms: this.forms,
            formsCount: this.formsCount,

            requiresConfirmation: this.requiresConfirmation,
            requiresClinicianApproval: this.requiresClinicianApproval,
            requiresClinicianEncounter: this.requiresClinicianEncounter,
            requiresSurveys: this.requiresSurveys,

            requiredDocuments: this.requiredDocuments,

            conversionId: this.conversionId,
            isConversion: this.isConversion,

            affiliateInfo: this.affiliateInfo,
            deviceInfo: this.deviceInfo,
            emailInfo: this.emailInfo,
            phoneInfo: this.phoneInfo,

            leadId: this.leadId,

            pharmacyId: this.pharmacyId,

            bundleShipmentCount: this.bundleShipmentCount,
            ordersPerEncounter: this.ordersPerEncounter,
            bundlePaymentCount: this.bundlePaymentCount,
            bundlePaymentCouponCode: this.bundlePaymentCouponCode,

            encounterType: this.encounterType,
            encounterFrequency: this.encounterFrequency,
            encounterPrice: this.encounterPrice,

            nextShipmentDate: this.nextShipmentDate,
            lastShipmentDate: this.lastShipmentDate,

            nextEncounterDate: this.nextEncounterDate,
            lastEncounterDate: this.lastEncounterDate,

            nextVariantLevel: this.nextVariantLevel,
            lastVariantLevel: this.lastVariantLevel,

            repId: this.repId,

            includesUpcharge: this.includesUpcharge,
            upcharge: this.upcharge,

            testMode: this.testMode,

            // Metadata
            metadata: this.metadata,

            // Test schedule
            testSchedule: this.testSchedule,

            // Annual cycle tracking
            cycleStartDate: this.cycleStartDate,
            currentCycleYear: this.currentCycleYear,
            baselineCompleted: this.baselineCompleted,

            // Next/last test tracking
            nextTestDate: this.nextTestDate,
            lastTestDate: this.lastTestDate,
            nextScheduleEntryId: this.nextScheduleEntryId,

            // Completed visits
            completedVisits: this.completedVisits,

            // Active treatment tracking
            activeTreatmentProductIds: this.activeTreatmentProductIds
        }
    }

    isValid = () => {
        if (isStringEmpty(this.userId)) {
            throw new Error('userId is required.');
        }
        if (isNumberEmpty(this.subtotal)) {
            throw new Error('subtotal is required.');
        }
        if (isNumberEmpty(this.tax)) {
            throw new Error('tax is required.');
        }
        if (isNumberEmpty(this.total)) {
            throw new Error('total is required.');
        }
        if (isNullOrUndefined(this.startDate)) {
            throw new Error('startDate is required.');
        }
        if (isStringEmpty(this.billingPeriod)) {
            throw new Error('billingPeriod is required.');
        }
        if (isNumberEmpty(this.billingInterval)) {
            throw new Error('billingInterval is required.');
        }
        if (isNumberEmpty(this.billingCycles)) {
            throw new Error('billingCycles is required.');
        }

        return true;
    }

    create = async () => {
        if (this.isValid()) {
            return await create(Subscription.collectionName, this.toDto());
        }
    }

    // a subscription in any other status holds its follow-up as it is rather than cancelling it, so the visit and
    // its reminders resume once the subscription is back in treatment
    static FOLLOW_UP_VISIT_STATUSES = ['ACTIVE', 'PHARMACY_CHANGED'];

    static canProceedWithFollowUpVisit = (subscription) => {
        return Subscription.FOLLOW_UP_VISIT_STATUSES.includes(subscription?.status);
    }

    // a renewal is charged only while the subscription is ACTIVE, and an order already raised - by the retry, Pay Now or
    // staff - while it is ACTIVE or SUSPENDED. every other status, including any added later, is never charged
    static SCHEDULED_CHARGE_STATUSES = ['ACTIVE'];
    static RETRY_CHARGE_STATUSES = ['ACTIVE', 'SUSPENDED'];

    // a PENDING subscription is collected only for its first order, whose capture failed
    static canChargeSubscription = (subscription, { isRetry = false, isFirstOrder = false } = {}) => {
        if (isBooleanTrue(isRetry) && isBooleanTrue(isFirstOrder) && subscription?.status === 'PENDING') {
            return true;
        }

        return (isBooleanTrue(isRetry) ? Subscription.RETRY_CHARGE_STATUSES : Subscription.SCHEDULED_CHARGE_STATUSES).includes(subscription?.status);
    }

    static updateField = async (id, fieldName, newValue) => {
        if (isStringNotEmpty(id)) {
            return await updateField(Subscription.collectionName, id, fieldName, newValue);
        } else {
            throw new Error("id is required.");
        }
    }

    static updateFields = async (id, updatedFields) => {
        if (isStringNotEmpty(id)) {
            return await updateFields(Subscription.collectionName, id, updatedFields);
        } else {
            throw new Error("id is required.");
        }
    }

    static update = async (id, updatedSubscription) => {
        const updatedObj = new Subscription(updatedSubscription);
        if (updatedObj.isValid() && id === updatedSubscription.id) {
            return await update(Subscription.collectionName, id, updatedObj.toDto());
        } else {
            throw new Error('id and id field of the updated subscription must match.')
        }
    }

    static delete = async (id) => {
        if (isStringNotEmpty(id)) {
            return await remove(Subscription.collectionName, id);
        } else {
            throw new Error("id is required.");
        }
    }

    static getById = async (id) => {
        if (isStringNotEmpty(id)) {
            return await getById(Subscription.collectionName, id);
        } else {
            throw new Error("id is required.");
        }
    }

    static getAll = async () => {
        return await getAll(Subscription.collectionName);
    }

    static getByField = async (fieldName, fieldValue, comparer = '==') => {
        return await getBySingleQuery(Subscription.collectionName, fieldName, fieldValue, comparer);
    }

    static getByUserId = async (userId) => {
        return await getBySingleQuery(Subscription.collectionName, 'userId', userId, '==');
    }

    static getByUserIdAndSubscriptionId = async (userId, subscriptionId) => {
        let userSubscriptions = await this.getByUserId(userId);
        if (isListNotEmpty(userSubscriptions)) {
            return userSubscriptions.find(sub => sub.id === subscriptionId) ?? null;
        }

        return null;
    }

    static getByCoveredEntityId = async (coveredEntityId) => {
        return await getBySingleQuery(Subscription.collectionName, 'coveredEntityId', coveredEntityId, '==');
    }

    static getByCoveredEntityIdAndUserId = async (coveredEntityId, userId) => {
        return await getByDoubleQuery(Subscription.collectionName, 'coveredEntityId', coveredEntityId, '==', 'userId', userId, '==');
    }

    static getByUserIdAndProgramId = async (userId, programId) => {
        return await getByDoubleQuery(Subscription.collectionName, 'userId', userId, '==', 'programId', programId, '==');
    }

    static getByUserIdAndStatus = async (userId, status) => {
        let queryConstraints = [];

        queryConstraints.push(where('userId', '==', userId));

        if (isListNotEmpty(status)) {
            queryConstraints.push(where('status', 'in', status));
        } else if (isStringNotEmptyOrZeroLength(status)) {
            queryConstraints.push(where('status', '==', status));
        }


        let q = query(collection(db, Subscription.collectionName), ...queryConstraints);

        let snapshot = await getDocs(q);
        return snapshot.docs.map((doc) => {
            return {
                id: doc.id,
                ...doc.data()
            };
        });
    }

    static getByUserIdAndProgramIdAndStatus = async (userId, programId, status) => {
        let queryConstraints = [];

        queryConstraints.push(where('userId', '==', userId));

        if (isListNotEmpty(programId)) {
            queryConstraints.push(where('programId', 'in', programId));
        } else if (isStringNotEmptyOrZeroLength(programId)) {
            queryConstraints.push(where('programId', '==', programId));
        }

        if (isListNotEmpty(status)) {
            queryConstraints.push(where('status', 'in', status));
        } else if (isStringNotEmptyOrZeroLength(status)) {
            queryConstraints.push(where('status', '==', status));
        }


        let q = query(collection(db, Subscription.collectionName), ...queryConstraints);

        let snapshot = await getDocs(q);
        return snapshot.docs.map((doc) => {
            return {
                id: doc.id,
                ...doc.data()
            };
        });
    }

    static getByIds = async (ids = []) => {
        return await getByIdQuery(Subscription.collectionName, ids, 'in');
    }

    static getByIdsAndUserId = async (ids = [], userId) => {
        return await getByIdDoubleQuery(Subscription.collectionName, ids, 'in', 'userId', userId, '==');
    }

    static getByQueryConstraints = async (queryConstraints = []) => {
        let q = query(collection(db, Subscription.collectionName), ...queryConstraints);

        const snapShot = await getDocs(q);
        // console.log(snapShot)
        return snapShot.docs.map((doc) => {
            return {
                id: doc.id, // append document id to each document
                ...doc.data()
            };
        });
    }

    static getByCoveredEntityIdAndPhone = async (coveredEntityId, phone) => {
        let queryConstraints = [];

        if (isStringNotEmptyOrZeroLength(coveredEntityId)) {
            queryConstraints.push(where('coveredEntityId', '==', coveredEntityId));
        }
        if (isStringNotEmptyOrZeroLength(phone)) {
            const cleanPhone = toPhoneNumberLocal(phone);
            const formattedPhone = toE164PhoneNumber(phone);
            queryConstraints.push(where('customer.phone', 'in', [phone, formattedPhone, cleanPhone]));
        }

        return await this.getByQueryConstraints(queryConstraints);
    }

    static getByCoveredEntityIdAndEmail = async (coveredEntityId, email) => {
        let queryConstraints = [];

        if (isStringNotEmptyOrZeroLength(coveredEntityId)) {
            queryConstraints.push(where('coveredEntityId', '==', coveredEntityId));
        }
        if (isStringNotEmptyOrZeroLength(email)) {
            queryConstraints.push(where('customer.email', '==', toLower(email)));
        }

        return await this.getByQueryConstraints(queryConstraints);
    }

    static getByCoveredEntityIdAndFirstNameAndLastNameAndEmailAndPhoneAndPatientId = async (coveredEntityId, firstName, lastName, email, phone, patientId) => {
        let queryConstraints = [];

        if (isStringNotEmptyOrZeroLength(coveredEntityId)) {
            queryConstraints.push(where('coveredEntityId', '==', coveredEntityId));
        }
        if (isStringNotEmptyOrZeroLength(firstName)) {
            queryConstraints.push(where('customer.firstNameLower', '==', toLower(firstName.trim())));
        }
        if (isStringNotEmptyOrZeroLength(lastName)) {
            queryConstraints.push(where('customer.lastNameLower', '==', toLower(lastName.trim())));
        }
        if (isStringNotEmptyOrZeroLength(email)) {
            queryConstraints.push(where('customer.email', '==', toLower(email.trim())));
        }
        if (isStringNotEmptyOrZeroLength(phone)) {
            const cleanPhone = toPhoneNumberLocal(phone.trim());
            const formattedPhone = toE164PhoneNumber(phone.trim());
            queryConstraints.push(where('customer.phone', 'in', [phone.trim(), formattedPhone, cleanPhone]));
        }
        if (isStringNotEmptyOrZeroLength(patientId)) {
            queryConstraints.push(where('userRefId', '==', patientId.trim()));
        }

        queryConstraints.push(orderBy('createdAt', 'asc'));
        queryConstraints.push(orderBy('__name__', 'asc'));

        return await this.getByQueryConstraints(queryConstraints);
    }

    static cancelSubscription = async (id, cancelScheduledEncounters = false) => {
        const subscription = await Subscription.getById(id);
        if (isObjectNotEmpty(subscription)) {
            let cancelledDate = moment.tz('US/Pacific').utc().toDate();

            // cancel all orders that havent been processed yet
            let ordersAreCancelled = true;
            const relatedOrders = await Order.getBySubscriptionId(subscription.id);
            if (isListNotEmpty(relatedOrders)) {
                for (const relatedOrder of relatedOrders) {
                    if (['DRAFT', 'CONFIRMED', 'APPROVED', 'READY', 'PENDING', 'FAILED', 'COLLECTIONS', 'SKIPPED'].includes(relatedOrder.status)) {
                        // allow order to be cancelled
                        let success = await Order.updateFields(relatedOrder.id, {
                            status: 'CANCELLED',
                            statusHistory: StatusHistory.addNewStatus('CANCELLED', relatedOrder.statusHistory, cancelledDate),
                            statusLastUpdatedAt: cancelledDate,
                            statusLastUpdatedBy: isObjectNotEmpty(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM'
                        });
                        if (isBooleanFalse(success)) {
                            ordersAreCancelled = false;
                        } else {
                            if (relatedOrder.orderType === 'PRESCRIPTION') {
                                // cancel the prescription as well
                                let prescription = await Prescription.getById(relatedOrder.prescriptionId);
                                if (isObjectNotEmpty(prescription)) {
                                    let updatedPrescriptionFields = {
                                        status: 'CANCELLED',
                                        statusHistory: StatusHistory.addNewStatus('CANCELLED', prescription.statusHistory)
                                    };
                                    await Prescription.updateFields(prescription.id, updatedPrescriptionFields);
                                }
                            }
                        }
                    }
                }
            }

            if (isBooleanTrue(cancelScheduledEncounters)) {
                const relatedEncounters = await Encounter.getBySubscriptionId(subscription.id);
                if (isListNotEmpty(relatedEncounters)) {
                    for (const relatedEncounter of relatedEncounters) {
                        if (['SCHEDULED', 'REQUESTED'].includes(relatedEncounter.status)) {
                            // allow encounter to be cancelled
                            await Encounter.updateFields(relatedEncounter.id, {
                                status: 'CANCELLED',
                                statusHistory: StatusHistory.addNewStatus('CANCELLED', relatedEncounter.statusHistory, cancelledDate),
                                cancelledAt: cancelledDate,
                                isCancelled: true
                            });
                        } else if (['STARTED', 'ASSIGNED'].includes(relatedEncounter.status)) {
                            // Beluga reports the real result inside the body, and `alreadyProcessed` still carries an
                            // authoritative outcome. An empty body means we never reached them, so leave the encounter
                            // as-is rather than recording a closure Beluga does not have.
                            const cancelEncounterResponse = await cancelBelugaVisit(relatedEncounter.refId, isBooleanTrue(relatedEncounter.testMode));
                            const cancelOutcome = isObjectNotEmpty(cancelEncounterResponse) && cancelEncounterResponse.status === 200 ? cancelEncounterResponse.outcome : null;

                            if (cancelOutcome === 'canceled') {
                                await Encounter.updateFields(relatedEncounter.id, {
                                    status: 'CANCELLED',
                                    statusHistory: StatusHistory.addNewStatus('CANCELLED', relatedEncounter.statusHistory, cancelledDate),
                                    cancelledAt: cancelledDate,
                                    isCancelled: true
                                });
                            } else if (cancelOutcome === 'resolved') {
                                // a doctor already prescribed, so Beluga closed the visit out instead of cancelling it
                                await Encounter.updateFields(relatedEncounter.id, {
                                    status: 'COMPLETED',
                                    statusHistory: StatusHistory.addNewStatus('COMPLETED', relatedEncounter.statusHistory, cancelledDate),
                                    completedAt: cancelledDate,
                                    isCompleted: true
                                });
                            } else {
                                // the visit may still be live at Beluga on a subscription we just cancelled, which is
                                // the billing exposure this flow exists to close, so it has to reach a human
                                console.log(`Unable to cancel Beluga visit ${relatedEncounter.refId} for encounter ${relatedEncounter.id}; encounter left unchanged`, cancelEncounterResponse);
                                await sendEmailToIT(
                                    `URGENT: Beluga visit ${relatedEncounter.refId} may still be open after a subscription was cancelled`,
                                    JSON.stringify({
                                        encounterId: relatedEncounter.id,
                                        encounterRefId: relatedEncounter.refId,
                                        encounterStatus: relatedEncounter.status,
                                        subscriptionId: subscription.id,
                                        subscriptionNumber: subscription.subscriptionNumber,
                                        cancelEncounterResponse: cancelEncounterResponse
                                    }, null, 2)
                                );
                            }
                        }
                    }
                }
            }

            // if the orders have been cancelled then cancel the subscription
            if (isBooleanTrue(ordersAreCancelled)) {
                let updatedFields = {
                    status: 'CANCELLED',
                    statusHistory: StatusHistory.addNewStatus('CANCELLED', subscription.statusHistory, cancelledDate),
                    cancelledDate: cancelledDate,
                    cancelledEmailSent: false,
                    nextPaymentDate: null
                };
                // cancel welcome call if subscription is being cancelled before welcome call is completed
                const terminalStatuses = ['COMPLETED', 'DECLINED', 'CANCELLED'];
                if (!terminalStatuses.includes(subscription.welcomeCallStatus)) {
                    updatedFields.welcomeCallStatus = 'CANCELLED';

                    try {
                        // mark welcome call task as cancelled
                        let matches = await Task.getByEntityIdAndEntityType(subscription.id, 'SUBSCRIPTION');
                        if (isListNotEmpty(matches)) {
                            let welcomeCallTask = matches.find(t => t.category === 'WELCOME' && !['COMPLETED', 'CANCELLED'].includes(t.status));
                            if (isObjectNotEmpty(welcomeCallTask)) {
                                let taskUpdateSuccess = await Task.updateFields(welcomeCallTask.id, {
                                    status: 'CANCELLED',
                                    statusHistory: StatusHistory.addNewStatus('CANCELLED', welcomeCallTask.statusHistory),
                                    completedAt: moment().utc().toDate()
                                });
                                if (isBooleanTrue(taskUpdateSuccess)) {
                                    console.log(`Welcome call task ${welcomeCallTask.id} marked as completed`);
                                }
                            }
                        }
                    } catch (err) {
                        console.log(`Error cancelling welcome call task for subscription ${subscription.id}:`, err);
                    }
                }

                let subscriptionSuccess = await Subscription.updateFields(subscription.id, updatedFields);
                if (isBooleanTrue(subscriptionSuccess) && isStringNotEmpty(subscription.userId)) {
                    try {
                        await UserNote.create(subscription.userId, {
                            body: `Cancelled Subscription via Subscription Management on ${fromUtcToTimeZone(cancelledDate, 'US/Pacific')}`,
                            at: cancelledDate,
                            by: subscription.userId
                        });
                    } catch (error) {
                        console.log(error);
                    }

                    try {
                        // send out cancellation email
                        await EmailTemplate.sendSubscriptionEmailToUser('SUBSCRIPTION_CANCELLED', subscription.customer, subscription, subscription.coveredEntityId, null);
                    } catch (error) {
                        console.log(error);
                    }
                }

                return subscriptionSuccess;
            }
        }

        return false;
    }

    static pauseSubscription = async (id) => {
        const subscription = await Subscription.getById(id);
        if (isObjectNotEmpty(subscription)) {
            let onHoldDate = moment.tz('US/Pacific').utc().toDate();

            // cancel or skip all orders that havent been processed yet
            let ordersAreOnHold = true;
            const relatedOrders = await Order.getBySubscriptionId(subscription.id);
            if (isListNotEmpty(relatedOrders)) {
                for (const relatedOrder of relatedOrders) {
                    if (['DRAFT', 'CONFIRMED', 'APPROVED', 'READY', 'PENDING', 'FAILED', 'COLLECTIONS'].includes(relatedOrder.status)) {
                        if (relatedOrder.orderType === 'VOUCHER') {
                            // cancel the voucher orders
                            let success = await Order.updateFields(relatedOrder.id, {
                                status: 'CANCELLED',
                                statusHistory: StatusHistory.addNewStatus('CANCELLED', relatedOrder.statusHistory, onHoldDate),
                                statusLastUpdatedAt: onHoldDate,
                                statusLastUpdatedBy: isObjectNotEmpty(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM'
                            });
                            if (isBooleanFalse(success)) {
                                ordersAreOnHold = false;
                            }
                        } else {
                            // skip the regular orders and prescription orders
                            let success = await Order.updateFields(relatedOrder.id, {
                                status: 'SKIPPED',
                                statusHistory: StatusHistory.addNewStatus('SKIPPED', relatedOrder.statusHistory, onHoldDate),
                                statusLastUpdatedAt: onHoldDate,
                                statusLastUpdatedBy: isObjectNotEmpty(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM'
                            });
                            if (isBooleanFalse(success)) {
                                ordersAreOnHold = false;
                            }
                        }
                    }
                }
            }

            // if the orders have been skipped then put the subscription on hold
            if (isBooleanTrue(ordersAreOnHold)) {
                let updatedFields = {
                    status: 'ON_HOLD',
                    statusHistory: StatusHistory.addNewStatus('ON_HOLD', subscription.statusHistory, onHoldDate)
                };
                let subscriptionSuccess = await Subscription.updateFields(subscription.id, updatedFields);

                return subscriptionSuccess;
            }
        }

        return false;
    }

    static getCount = async (queryParameters, dateFieldName = 'createdAt') => {
        const { state, testMode, userId, repId, programId, status, coveredEntityId, pharmacyId, bundleShipmentCount, bundlePaymentCount, productSku, productId, variantId, affiliateId, start, end } = queryParameters;

        let queryConstraints = [];

        if (isBooleanTrue(testMode)) {
            queryConstraints.push(where('testMode', '==', true));
        } else if (isBooleanFalse(testMode)) {
            queryConstraints.push(where('testMode', '==', false));
        }

        if (isListNotEmpty(userId)) {
            queryConstraints.push(where('userId', 'in', userId));
        } else if (isStringNotEmptyOrZeroLength(userId)) {
            queryConstraints.push(where('userId', '==', userId));
        }

        if (isListNotEmpty(programId)) {
            queryConstraints.push(where('programId', 'in', programId));
        } else if (isStringNotEmptyOrZeroLength(programId)) {
            queryConstraints.push(where('programId', '==', programId));
        }

        if (isListNotEmpty(status)) {
            queryConstraints.push(where('status', 'in', status));
        } else if (isStringNotEmptyOrZeroLength(status)) {
            queryConstraints.push(where('status', '==', status));
        }

        if (isListNotEmpty(coveredEntityId)) {
            queryConstraints.push(where('coveredEntityId', 'in', coveredEntityId));
        } else if (isStringNotEmptyOrZeroLength(coveredEntityId)) {
            queryConstraints.push(where('coveredEntityId', '==', coveredEntityId));
        }

        if (isListNotEmpty(pharmacyId)) {
            queryConstraints.push(where('pharmacyId', 'in', pharmacyId));
        } else if (isStringNotEmptyOrZeroLength(pharmacyId)) {
            queryConstraints.push(where('pharmacyId', '==', pharmacyId));
        }

        if (isListNotEmpty(bundlePaymentCount)) {
            queryConstraints.push(where('bundlePaymentCount', 'in', bundlePaymentCount));
        } else if (isNumberNotEmpty(bundlePaymentCount)) {
            queryConstraints.push(where('bundlePaymentCount', '==', bundlePaymentCount));
        }

        if (isListNotEmpty(bundleShipmentCount)) {
            queryConstraints.push(where('bundleShipmentCount', 'in', bundleShipmentCount));
        } else if (isNumberNotEmpty(bundleShipmentCount)) {
            queryConstraints.push(where('bundleShipmentCount', '==', bundleShipmentCount));
        }

        if (isListNotEmpty(state)) {
            queryConstraints.push(where('shippingAddress.state', 'in', state));
        } else if (isStringNotEmptyOrZeroLength(state)) {
            queryConstraints.push(where('shippingAddress.state', '==', state));
        }

        if (isListNotEmpty(productSku)) {
            queryConstraints.push(where('productSku', 'in', productSku));
        } else if (isStringNotEmptyOrZeroLength(productSku)) {
            queryConstraints.push(where('productSku', '==', productSku));
        }

        if (isListNotEmpty(productId)) {
            queryConstraints.push(where('productId', 'in', productId));
        } else if (isStringNotEmptyOrZeroLength(productId)) {
            queryConstraints.push(where('productId', '==', productId));
        }

        if (isListNotEmpty(variantId)) {
            queryConstraints.push(where('variantId', 'in', variantId));
        } else if (isStringNotEmptyOrZeroLength(variantId)) {
            queryConstraints.push(where('variantId', '==', variantId));
        }

        if (isListNotEmpty(affiliateId)) {
            queryConstraints.push(where('affiliateId', 'in', affiliateId));
        } else if (isStringNotEmptyOrZeroLength(affiliateId)) {
            queryConstraints.push(where('affiliateId', '==', affiliateId));
        }

        if (isListNotEmpty(repId)) {
            queryConstraints.push(where('repId', 'in', repId));
        } else if (isStringNotEmptyOrZeroLength(repId)) {
            if (repId === 'NONE') {
                queryConstraints.push(where('repId', '==', 'NONE'));
            } else if (repId === 'ASSIGNED') {
                queryConstraints.push(where('repId', '!=', 'NONE'));
            } else {
                queryConstraints.push(where('repId', '==', repId));
            }
        }

        if (isNotNullOrUndefined(start)) {
            let startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
            queryConstraints.push(where(dateFieldName, '>=', startDate));
        }

        if (isNotNullOrUndefined(end)) {
            let endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
            queryConstraints.push(where(dateFieldName, '<=', endDate));
        }

        let q = query(collection(db, Subscription.collectionName), ...queryConstraints);

        const snapShot = await getCountFromServer(q);

        //console.log(snapShot.data());

        return snapShot.data().count;
    }

    static getPaginated = async (queryParameters, pageSize = 25, firstVisible, lastVisible, orderByField = 'createdAt', dateFieldName = 'createdAt') => {
        const { state, testMode, userId, repId, programId, status, coveredEntityId, pharmacyId, bundleShipmentCount, bundlePaymentCount, productSku, productId, variantId, affiliateId, start, end } = queryParameters;

        let queryConstraints = [];

        if (isBooleanTrue(testMode)) {
            queryConstraints.push(where('testMode', '==', true));
        } else if (isBooleanFalse(testMode)) {
            queryConstraints.push(where('testMode', '==', false));
        }

        if (isListNotEmpty(userId)) {
            queryConstraints.push(where('userId', 'in', userId));
        } else if (isStringNotEmptyOrZeroLength(userId)) {
            queryConstraints.push(where('userId', '==', userId));
        }

        if (isListNotEmpty(programId)) {
            queryConstraints.push(where('programId', 'in', programId));
        } else if (isStringNotEmptyOrZeroLength(programId)) {
            queryConstraints.push(where('programId', '==', programId));
        }

        if (isListNotEmpty(status)) {
            queryConstraints.push(where('status', 'in', status));
        } else if (isStringNotEmptyOrZeroLength(status)) {
            queryConstraints.push(where('status', '==', status));
        }

        if (isListNotEmpty(coveredEntityId)) {
            queryConstraints.push(where('coveredEntityId', 'in', coveredEntityId));
        } else if (isStringNotEmptyOrZeroLength(coveredEntityId)) {
            queryConstraints.push(where('coveredEntityId', '==', coveredEntityId));
        }

        if (isListNotEmpty(pharmacyId)) {
            queryConstraints.push(where('pharmacyId', 'in', pharmacyId));
        } else if (isStringNotEmptyOrZeroLength(pharmacyId)) {
            queryConstraints.push(where('pharmacyId', '==', pharmacyId));
        }

        if (isListNotEmpty(bundlePaymentCount)) {
            queryConstraints.push(where('bundlePaymentCount', 'in', bundlePaymentCount));
        } else if (isNumberNotEmpty(bundlePaymentCount)) {
            queryConstraints.push(where('bundlePaymentCount', '==', bundlePaymentCount));
        }

        if (isListNotEmpty(bundleShipmentCount)) {
            queryConstraints.push(where('bundleShipmentCount', 'in', bundleShipmentCount));
        } else if (isNumberNotEmpty(bundleShipmentCount)) {
            queryConstraints.push(where('bundleShipmentCount', '==', bundleShipmentCount));
        }

        if (isListNotEmpty(state)) {
            queryConstraints.push(where('shippingAddress.state', 'in', state));
        } else if (isStringNotEmptyOrZeroLength(state)) {
            queryConstraints.push(where('shippingAddress.state', '==', state));
        }

        if (isListNotEmpty(productSku)) {
            queryConstraints.push(where('productSku', 'in', productSku));
        } else if (isStringNotEmptyOrZeroLength(productSku)) {
            queryConstraints.push(where('productSku', '==', productSku));
        }

        if (isListNotEmpty(productId)) {
            queryConstraints.push(where('productId', 'in', productId));
        } else if (isStringNotEmptyOrZeroLength(productId)) {
            queryConstraints.push(where('productId', '==', productId));
        }

        if (isListNotEmpty(variantId)) {
            queryConstraints.push(where('variantId', 'in', variantId));
        } else if (isStringNotEmptyOrZeroLength(variantId)) {
            queryConstraints.push(where('variantId', '==', variantId));
        }

        if (isListNotEmpty(affiliateId)) {
            queryConstraints.push(where('affiliateId', 'in', affiliateId));
        } else if (isStringNotEmptyOrZeroLength(affiliateId)) {
            queryConstraints.push(where('affiliateId', '==', affiliateId));
        }

        if (isListNotEmpty(repId)) {
            queryConstraints.push(where('repId', 'in', repId));
        } else if (isStringNotEmptyOrZeroLength(repId)) {
            if (repId === 'NONE') {
                queryConstraints.push(where('repId', '==', 'NONE'));
            } else if (repId === 'ASSIGNED') {
                queryConstraints.push(where('repId', '!=', 'NONE'));
            } else {
                queryConstraints.push(where('repId', '==', repId));
            }
        }

        if (isNotNullOrUndefined(start)) {
            let startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
            queryConstraints.push(where(dateFieldName, '>=', startDate));
        }

        if (isNotNullOrUndefined(end)) {
            let endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
            queryConstraints.push(where(dateFieldName, '<=', endDate));
        }

        let q = null;
        if (isObjectNotEmpty(firstVisible)) {
            q = query(collection(db, Subscription.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('repId', 'asc'), orderBy('__name__', 'asc'), limitToLast(pageSize), endBefore(firstVisible[orderByField], firstVisible['repId'], firstVisible['id']));
        } else if (isObjectNotEmpty(lastVisible)) {
            q = query(collection(db, Subscription.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('repId', 'asc'), orderBy('__name__', 'asc'), limit(pageSize), startAfter(lastVisible[orderByField], lastVisible['repId'], lastVisible['id']));
        } else {
            q = query(collection(db, Subscription.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('repId', 'asc'), orderBy('__name__', 'asc'), limit(pageSize));
        }

        let snapshot = await getDocs(q);
        return snapshot.docs.map((doc) => {
            return {
                id: doc.id,
                ...doc.data()
            };
        });
    }

    static get = async (queryParameters, dateFieldName = 'createdAt') => {
        let allRecords = [];
        try {
            let next = null;

            let totalCount = await Subscription.getCount(queryParameters, dateFieldName);
            let totalPages = Math.ceil(totalCount / 10000.0);
            // console.log('totalCount: ' + totalCount);
            // console.log('totalPages: ' + totalPages);
            let pages = [];
            for (let i = 0; i < totalPages; i++) {
                pages.push(i + 1);
            }
            for (const page of pages) {
                // console.log('starting page ' + page);

                let list = await Subscription.getPaginated(queryParameters, 10000, null, next, dateFieldName, dateFieldName);

                // console.log(list.length);
                allRecords = [...allRecords, ...list];

                // console.log(allRecords.length);

                // console.log('go to next page');
                next = list[list.length - 1];
            }
        } catch (error) {
            console.log(error);
        }

        return allRecords;
    }
};

export default Subscription;
